import { useState, useEffect } from 'react';
import { ChipTipoTramite } from '../../components/ChipsTramitesCliente';
import { getTramites, getResumenTramites, eliminarTramite } from '../../services/tramiteService';
import { useNavigate, useLocation } from 'react-router-dom';
import { getEstados } from '../../services/estadoService';
import {
    Snackbar,
    Alert,
    Pagination
} from '@mui/material';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { formatCuil } from '../../utils/formatCuil';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
import PageHeader from '../../components/ui/PageHeader';

function ListadoTramitesRTI({ initialIdTipoTramite = null, showOrdenFecha = false }) {
    const valorInicialTipo = (initialIdTipoTramite != null && String(initialIdTipoTramite).trim() !== '')
        ? String(initialIdTipoTramite)
        : '1';

    const [tramites, setTramites] = useState([]);
    const [resumen, setResumen] = useState({ total: 0, enProceso: 0, finalizados: 0 });
    const [page, setPage] = useFiltroPersistente('tramitesRTI.page', 0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filtroIdCliente, setFiltroIdCliente] = useFiltroPersistente('tramitesRTI.filtroIdCliente', '');
    const [filtroNombreApellido, setFiltroNombreApellido] = useFiltroPersistente('tramitesRTI.filtroNombreApellido', '');
    const [dialogoEliminar, setDialogoEliminar] = useState({ open: false, idTramite: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filtroEstado, setFiltroEstado] = useFiltroPersistente('tramitesRTI.filtroEstado', '');
    const [filtroOrganismo, setFiltroOrganismo] = useFiltroPersistente('tramitesRTI.filtroOrganismo', '');
    const [estados, setEstados] = useState([]);
    const [filtroTipoTramite, setFiltroTipoTramite] = useFiltroPersistente('tramitesRTI.filtroTipoTramite', valorInicialTipo);
    /** Orden por fecha (solo cuando showOrdenFecha, ej. en ART): 'desc' = más recientes primero, 'asc' = más antiguos primero */
    const [ordenFecha, setOrdenFecha] = useState('desc');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const navigate = useNavigate();
    const location = useLocation();

    // Filtro inicial desde prop (ej. área ART en /art): mantener sincronizado
    useEffect(() => {
        if (initialIdTipoTramite != null && String(initialIdTipoTramite).trim() !== '') {
            setFiltroTipoTramite(String(initialIdTipoTramite));
        }
    }, [initialIdTipoTramite]);

    // Aplicar filtro de área al entrar desde Home (RTI, ART, etc.) solo si NO venimos de /art con prop
    useEffect(() => {
        if (initialIdTipoTramite != null) return; // ya tenemos filtro por prop
        const idFromState = location.state?.idTipoTramite;
        if (idFromState != null && String(idFromState) !== '') {
            setFiltroTipoTramite(String(idFromState));
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.idTipoTramite, initialIdTipoTramite]);

    // Función para formatear CUIL con formato argentino (XX-XXXXXXXX-X)
    const formatearCUIL = (cuil) => {
        if (!cuil || cuil === 'N/A') return 'N/A';
        
        // Remover cualquier carácter que no sea número
        const numeros = cuil.replace(/\D/g, '');
        
        // Verificar que tenga 11 dígitos
        if (numeros.length !== 11) return cuil;
        
        // Formatear como XX-XXXXXXXX-X
        return `${numeros.slice(0, 2)}-${numeros.slice(2, 10)}-${numeros.slice(10)}`;
    };

    const obtenerUltimoComentario = (texto) => {
        if (!texto) return 'Sin comentarios';
        const lineas = texto.split('\n').filter(linea => linea.trim() !== '');
        return lineas.length > 0 ? lineas[lineas.length - 1] : 'Sin comentarios';
    };

    const cargarTramites = async () => {
        setLoading(true);
        try {
            const data = await getTramites(
                page,
                15,
                filtroIdCliente?.trim() || '',
                filtroTipoTramite || valorInicialTipo || null,
                filtroEstado || null,
                filtroNombreApellido?.trim() || '',
                filtroOrganismo || null
            );
            setTramites(data.content || []);
            setTotalPages(data.totalPages || 0);
        } catch (e) {
            console.error("Error al cargar trámites:", e);
            setSnackbar({ open: true, message: 'Error al cargar trámites', severity: 'error' });
        }
        setLoading(false);
    };

    const cargarResumen = async () => {
        try {
            const data = await getResumenTramites(
                filtroIdCliente?.trim() || '',
                filtroNombreApellido?.trim() || '',
                filtroTipoTramite || valorInicialTipo || null,
                filtroOrganismo || null
            );
            setResumen({
                total: data?.total || 0,
                enProceso: data?.enProceso || 0,
                finalizados: data?.finalizados || 0,
            });
        } catch (e) {
            console.error('Error al cargar resumen de trámites:', e);
        }
    };

    // Solo mostramos los estados del ámbito RTI
    const cargarEstados = async () => {
        try {
            const todos = await getEstados();
            setEstados((todos || []).filter((e) => e.ambito === 'RTI' || (e.nombre || '').toLowerCase() === 'cancelado'));
        } catch (e) {
            console.error("Error al cargar estados:", e);
        }
    };

    useEffect(() => {
        cargarEstados();
    }, []);



    // Cada vez que cambie paginación/filtros, recargamos desde servidor
    useEffect(() => {
        cargarTramites();
        cargarResumen();
    }, [page, filtroIdCliente, filtroTipoTramite, filtroEstado, filtroNombreApellido, filtroOrganismo]);

    const parseFecha = (fechaStr) => {
        if (!fechaStr) return 0;
        const d = new Date(fechaStr);
        return isNaN(d.getTime()) ? 0 : d.getTime();
    };

    const tramitesParaTabla = showOrdenFecha
        ? [...tramites].sort((a, b) => {
            const ta = parseFecha(a.fechaEstadoActual);
            const tb = parseFecha(b.fechaEstadoActual);
            return ordenFecha === 'desc' ? tb - ta : ta - tb;
          })
        : tramites;

    const invertirOrden = () => setOrdenFecha((o) => (o === 'desc' ? 'asc' : 'desc'));

    const editarTramite = (idTramite) => navigate(`/rti/tramites/${idTramite}`);

    const abrirDialogoEliminar = (idTramite) => setDialogoEliminar({ open: true, idTramite });
    const cerrarDialogoEliminar = () => setDialogoEliminar({ open: false, idTramite: null });

    const confirmarEliminacion = async () => {
        if (dialogoEliminar.idTramite) {
            try {
                await eliminarTramite(dialogoEliminar.idTramite);
                setSnackbar({ open: true, message: 'Trámite eliminado correctamente', severity: 'success' });
                cerrarDialogoEliminar();
                cargarTramites();
                cargarResumen();
            } catch (error) {
                setSnackbar({ open: true, message: 'Error al eliminar el trámite', severity: 'error' });
            }
        }
    };

    return (
        <div className="area-rti">
            <div className="container-fluid mt-4">
                <PageHeader
                    variante="plano"
                    icono="folder-fill"
                    titulo="Trámites RTI"
                    descripcion="Gestión y seguimiento de trámites previsionales"
                    accion={
                        <div className="d-flex align-items-center gap-2">
                            {showOrdenFecha && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={invertirOrden}
                                    title={ordenFecha === 'desc' ? 'Ver más antiguos primero' : 'Ver más recientes primero'}
                                    style={{ borderRadius: '8px', padding: '0.5rem 1rem', fontWeight: '500' }}
                                >
                                    <i className={`bi bi-arrow-${ordenFecha === 'desc' ? 'down' : 'up'}-short me-1`} />
                                    {ordenFecha === 'desc' ? 'Más recientes primero' : 'Más antiguos primero'}
                                </button>
                            )}
                            <button
                                className="btn btn-primary d-flex align-items-center gap-2"
                                onClick={() => navigate('/rti/tramites/iniciar-tramite')}
                                style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                            >
                                <i className="bi bi-plus-circle me-1" />
                                Iniciar Trámite
                            </button>
                        </div>
                    }
                />

                {/* Estadísticas Cards */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <TarjetaKpi label="Total trámites" valor={resumen.total} icono="folder2-open" />
                    </div>
                    <div className="col-md-4">
                        <TarjetaKpi tono="advertencia" label="En proceso" valor={resumen.enProceso} icono="hourglass-split" />
                    </div>
                    <div className="col-md-4">
                        <TarjetaKpi tono="exito" label="Finalizados" valor={resumen.finalizados} icono="check-circle-fill" />
                    </div>
                </div>

                {/* Filtros Modernos */}
                {(() => {
                    const campoCuil = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-search me-2"></i>Buscar por CUIL
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-card-text" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ingrese CUIL del cliente"
                                    value={filtroIdCliente}
                                    maxLength="13"
                                    onChange={(e) => {
                                        setFiltroIdCliente(formatCuil(e.target.value));
                                        setPage(0);
                                    }}
                                    style={{ borderLeft: 'none' }}
                                />
                            </div>
                        </>
                    );
                    const campoNombre = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-person me-2"></i>Buscar por Nombre/Apellido
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nombre o apellido del cliente"
                                    value={filtroNombreApellido}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroNombreApellido(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none' }}
                                />
                            </div>
                        </>
                    );
                    const campoEstado = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-list-check me-2"></i>Estado del Trámite
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-flag" style={{ color: '#6B7280' }}></i>
                                </span>
                                <select
                                    className="form-select"
                                    value={filtroEstado}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroEstado(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none' }}
                                >
                                    <option value="">Todos los estados</option>
                                    {estados.map((estado) => (
                                        <option key={estado.id} value={estado.id}>
                                            {estado.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    );
                    const campoOrganismo = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-building me-2"></i>Organismo
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-bank" style={{ color: '#6B7280' }}></i>
                                </span>
                                <select
                                    className="form-select"
                                    value={filtroOrganismo}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroOrganismo(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none' }}
                                >
                                    <option value="">Todos</option>
                                    <option value="PROVINCIA">Provincia</option>
                                    <option value="ANSES">ANSES</option>
                                </select>
                            </div>
                        </>
                    );

                    const limpiarFiltros = () => {
                        setPage(0);
                        setFiltroIdCliente('');
                        setFiltroNombreApellido('');
                        setFiltroEstado('');
                        setFiltroOrganismo('');
                        setFiltroTipoTramite(valorInicialTipo);
                    };

                    const chipsFiltros = [
                        filtroIdCliente && { key: 'cuil', label: `CUIL: ${filtroIdCliente}`, onQuitar: () => { setPage(0); setFiltroIdCliente(''); } },
                        filtroNombreApellido && { key: 'nombre', label: filtroNombreApellido, onQuitar: () => { setPage(0); setFiltroNombreApellido(''); } },
                        filtroEstado && {
                            key: 'estado',
                            label: estados.find((e) => String(e.id) === String(filtroEstado))?.nombre || 'Estado',
                            onQuitar: () => { setPage(0); setFiltroEstado(''); },
                        },
                        filtroOrganismo && { key: 'organismo', label: filtroOrganismo === 'PROVINCIA' ? 'Provincia' : 'ANSES', onQuitar: () => { setPage(0); setFiltroOrganismo(''); } },
                    ].filter(Boolean);

                    return esMobile ? (
                        <div className="mb-4">
                            <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                                >
                                    <div className="mb-3">{campoCuil}</div>
                                    <div className="mb-3">{campoNombre}</div>
                                    <div className="mb-3">{campoEstado}</div>
                                    <div>{campoOrganismo}</div>
                                </BottomSheet>
                            )}
                        </div>
                    ) : (
                        <div className="card shadow-sm mb-4" style={{ border: 'none', borderRadius: '12px' }}>
                            <div className="card-body" style={{ padding: '1.5rem' }}>
                                <div className="row g-3">
                                    <div className="col-lg-4 col-md-6">{campoCuil}</div>
                                    <div className="col-lg-4 col-md-6">{campoNombre}</div>
                                    <div className="col-lg-4 col-md-6">{campoEstado}</div>
                                    <div className="col-lg-4 col-md-6">{campoOrganismo}</div>
                                </div>

                                {/* Botón limpiar filtros */}
                                <div className="text-end mt-3">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={limpiarFiltros}
                                        style={{ borderRadius: '8px', padding: '0.5rem 1.5rem', fontWeight: '500' }}
                                    >
                                        <i className="bi bi-arrow-clockwise me-2"></i>Limpiar filtros
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
                        <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámites...</p>
                    </div>
                ) : (
                    <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 tabla-listado-ajustada">
                                <thead style={{ background: 'var(--gradient-primary-light)', borderBottom: '2px solid #3B82F6' }}>
                                    <tr>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            CUIL Cliente
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            Nombre
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            Tipo
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            Estado
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-calendar-event me-2"></i>Fecha
                                        </th>
                                        <th className="text-center col-comentarios" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', minWidth: '100px' }}>
                                            <i className="bi bi-chat-text me-2"></i>Comentarios
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-gear me-2"></i>Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                {tramitesParaTabla.map((t) => {
                                    return (
                                        <tr 
                                            key={t.idTramite}
                                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                <span className="badge" style={{ background: '#eff6ff', color: '#1e40af', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem' }}>
                                                    {formatCuil(t.cuilCliente)}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', fontWeight: '500', color: '#374151' }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                                    <span>{[t.apellidoCliente, t.nombreCliente].filter(Boolean).join(', ') || 'N/A'}</span>
                                                    {t.organismo === 'ANSES' && (
                                                        <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>ANSES</span>
                                                    )}
                                                    {t.organismo === 'PROVINCIA' && (
                                                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Provincia</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', color: '#6B7280' }}>
                                                <ChipTipoTramite tipo={t.tipoTramite} />
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', whiteSpace: 'nowrap' }}>
                                                <span 
                                                    className="badge" 
                                                    style={{ 
                                                        background: t.nombreEstadoActual === 'Finalizado' ? '#dcfce7' : 
                                                                   t.nombreEstadoActual === 'Rechazado' ? '#fee2e2' : '#fef3c7',
                                                        color: t.nombreEstadoActual === 'Finalizado' ? '#166534' : 
                                                               t.nombreEstadoActual === 'Rechazado' ? '#991b1b' : '#92400e',
                                                        padding: '0.5rem 0.8rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {t.nombreEstadoActual}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem', color: '#6B7280' }}>
                                                {t.fechaEstadoActual ? new Date(t.fechaEstadoActual).toLocaleDateString('es-ES') : 'N/A'}
                                            </td>
                                            <td
                                                className="col-comentarios" style={{ padding: '1rem', wordBreak: 'break-word', color: '#6B7280', fontSize: '0.9rem' }}
                                                title={t.comentario || t.comentarios || 'Sin comentarios'}
                                            >
                                                {obtenerUltimoComentario(t.comentario || t.comentarios)}
                                            </td>
                                            <td className="text-center" style={{ padding: '1rem' }}>
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => editarTramite(t.idTramite)}
                                                    style={{ borderRadius: '6px', fontWeight: '500', padding: '0.4rem 1rem' }}
                                                >
                                                    <i className="bi bi-eye me-1"></i>Ver detalles
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                            {tramitesParaTabla.length === 0 && (
                                <div className="text-center py-5">
                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                                    <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No se encontraron trámites.</p>
                                </div>
                            )}

                            {totalPages > 0 && (
                                <div className="d-flex justify-content-center py-3 border-top">
                                    <Pagination
                                        count={totalPages}
                                        page={page + 1}
                                        onChange={(_e, value) => setPage(value - 1)}
                                        color="primary"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 🔸 Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default ListadoTramitesRTI;
